import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, X } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { Appointment, APPOINTMENT_TYPE_LABELS, AppointmentType } from '../types/appointment';
import { Patient } from '../types/patient';

interface Props {
  defaultDate: string;
  defaultTime: string;
  onClose: () => void;
  onCreated: () => void;
  initialPatient?: Patient | null;
}

const TYPES: AppointmentType[] = ['consultation', 'follow_up', 'antenatal', 'ultrasound', 'procedure', 'emergency'];
const DURATIONS = [15, 20, 30, 45, 60, 90, 120];

export function NewAppointmentModal({ defaultDate, defaultTime, onClose, onCreated, initialPatient }: Props) {
  const { get, post } = useApi();

  const [patientSearch, setPatientSearch] = useState('');
  const [patients, setPatients] = useState<Patient[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(initialPatient || null);
  const [searchLoading, setSearchLoading] = useState(false);
  const patientLocked = !!initialPatient;

  const [date, setDate] = useState(defaultDate);
  const [startTime, setStartTime] = useState(defaultTime);
  const [duration, setDuration] = useState(30);
  const [apptType, setApptType] = useState<AppointmentType>('consultation');
  const [notes, setNotes] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState<Appointment | null>(null);

  // Patient search
  useEffect(() => {
    if (patientSearch.length < 2) {
      setPatients([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const result = await get<Patient[]>(`/patients?search=${encodeURIComponent(patientSearch)}&activeOnly=true`);
        if (result.success && result.data) {
          setPatients(result.data);
        }
      } catch { /* ignore */ }
      setSearchLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [patientSearch, get]);

  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedPatient) {
      setError('Please select a patient');
      return;
    }
    setError('');
    setConflict(null);
    setSubmitting(true);

    try {
      const result = await post<Appointment>('/appointments', {
        patientId: selectedPatient.id,
        date,
        startTime,
        durationMinutes: duration,
        appointmentType: apptType,
        notes,
      });

      if (result.success) {
        onCreated();
      } else {
        setError(result.error || 'Failed to create appointment');
        const data = result.data as unknown as { conflict?: Appointment } | null;
        if (data?.conflict) {
          setConflict(data.conflict);
        }
      }
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Failed to create appointment');
      }
    } finally {
      setSubmitting(false);
    }
  }, [selectedPatient, date, startTime, duration, apptType, notes, post, onCreated]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">New Appointment</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {error && (
            <div className="p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
          )}
          {conflict && (
            <div className="p-3 rounded bg-amber-50 border border-amber-200 text-amber-700 text-sm">
              Conflict: {conflict.patientName || 'Block-out'} at {conflict.startTime}–{conflict.endTime}
            </div>
          )}

          {/* Patient search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Patient *</label>
            {selectedPatient ? (
              <div className="flex items-center justify-between px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <span className="text-sm font-medium text-blue-900">
                  {selectedPatient.title} {selectedPatient.firstName} {selectedPatient.lastName}
                </span>
                {!patientLocked && (
                  <button type="button" onClick={() => { setSelectedPatient(null); setPatientSearch(''); }} className="text-blue-600 hover:text-blue-800 text-xs">
                    Change
                  </button>
                )}
              </div>
            ) : (
              <div className="relative">
                <input
                  type="text"
                  value={patientSearch}
                  onChange={e => setPatientSearch(e.target.value)}
                  placeholder="Search by name or ID number..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
                {searchLoading && <Loader2 className="absolute right-3 top-2.5 h-4 w-4 animate-spin text-gray-400" />}
                {patients.length > 0 && (
                  <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {patients.map(p => (
                      <li key={p.id}>
                        <button
                          type="button"
                          onClick={() => { setSelectedPatient(p); setPatients([]); setPatientSearch(''); }}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm"
                        >
                          <span className="font-medium">{p.title} {p.firstName} {p.lastName}</span>
                          <span className="text-gray-500 ml-2">{p.idNumber}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
              <input
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Time *</label>
              <input
                type="time"
                value={startTime}
                onChange={e => setStartTime(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Duration & Type */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
              <select
                value={duration}
                onChange={e => setDuration(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {DURATIONS.map(d => (
                  <option key={d} value={d}>{d} min</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
              <select
                value={apptType}
                onChange={e => setApptType(e.target.value as AppointmentType)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {TYPES.map(t => (
                  <option key={t} value={t}>{APPOINTMENT_TYPE_LABELS[t]}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              rows={2}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={submitting} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating...</> : 'Create Appointment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
